import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { formatMoney, nodes } from "@/lib/shopify";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, busy } = useCart();
  const images = nodes(product.images);
  const second = images[1]?.url;
  const variants = nodes(product.variants);
  const firstAvailable = variants.find((v) => v.availableForSale) || variants[0];
  const price = product.priceRange?.minVariantPrice;
  const compareAt = product.compareAtPriceRange?.minVariantPrice;
  const onSale =
    compareAt && price && parseFloat(compareAt.amount) > parseFloat(price.amount);

  return (
    <motion.div
      data-testid={`product-card-${product.handle}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl border border-white/10 bg-obsidian">
        <Link to={`/products/${product.handle}`} data-testid={`product-card-link-${product.handle}`}>
          {product.featuredImage?.url ? (
            <img
              src={product.featuredImage.url}
              alt={product.featuredImage.altText || product.title}
              loading="lazy"
              className={`h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-105 ${
                second ? "group-hover:opacity-0" : ""
              }`}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-mono2 text-xs text-ivory/30">
              NO IMAGE
            </div>
          )}
          {second && (
            <img
              src={second}
              alt={images[1]?.altText || product.title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-100"
            />
          )}
        </Link>

        {onSale && (
          <span className="absolute left-3 top-3 rounded-full bg-amber px-3 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-void">
            On sale
          </span>
        )}
        {!product.availableForSale && (
          <span className="absolute left-3 top-3 rounded-full bg-ivory/10 px-3 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-ivory/70 backdrop-blur">
            Sold out
          </span>
        )}

        {product.availableForSale && firstAvailable && (
          <button
            data-testid={`product-card-add-to-cart-${product.handle}`}
            onClick={() => addToCart(firstAvailable.id, 1)}
            disabled={busy}
            className="absolute bottom-3 right-3 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-amber text-void opacity-0 transition-all duration-300 hover:scale-110 group-hover:translate-y-0 group-hover:opacity-100 disabled:opacity-50"
            aria-label={`Add ${product.title} to cart`}
          >
            <Plus className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <Link
          to={`/products/${product.handle}`}
          className="font-display text-sm font-bold leading-snug text-ivory transition-colors hover:text-amber"
        >
          {product.title}
        </Link>
        <div className="shrink-0 text-right">
          <p className="font-mono2 text-sm font-bold text-amber">{formatMoney(price)}</p>
          {onSale && (
            <p className="font-mono2 text-xs text-ivory/40 line-through">
              {formatMoney(compareAt)}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
